import React from 'react';

import { Box, FormControlLabel, Tooltip } from '@material-ui/core';
import clsx from 'clsx';

import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { DetailsIcon } from '@vyce/core/src/components/DetailsIcon';
import { PermissionDTO, PermissionGroupDTO } from '@vyce/core/src/api/types';

import { useRolesStyles } from '../styles';
import { CatalogueIndex, lockReasons } from '../utils/permissionState';

interface Props {
  groups: PermissionGroupDTO[];
  /** Ticked boxes: role defaults with this person's exceptions folded in. */
  effective: Set<string>;
  /** What the user ticked themselves — the rest of `effective` is locked. */
  explicit: Set<string>;
  /** The set the ticks are diffed against: role defaults, or a module default. */
  defaults: string[];
  added?: string[];
  removed?: string[];
  conflictNames?: Set<string>;
  disabled?: boolean;
  catalogue: CatalogueIndex;
  onToggle: (systemName: string, checked: boolean) => void;
}

export const PermissionCheckList: React.FC<Props> = ({
  groups,
  effective,
  explicit,
  defaults,
  added,
  removed,
  conflictNames,
  disabled,
  catalogue,
  onToggle,
}) => {
  const classes = useRolesStyles();
  const defaultSet = new Set(defaults);
  const addedSet = new Set(added || []);
  const removedSet = new Set(removed || []);

  const renderRow = (permission: PermissionDTO) => {
    const checked = effective.has(permission.system_name);
    const locked = checked && !explicit.has(permission.system_name);
    const reasons = locked ? lockReasons(permission.system_name, explicit, catalogue) : [];
    const isConflict = !!conflictNames?.has(permission.system_name);
    const changedFromDefault = checked !== defaultSet.has(permission.system_name);

    const row = (
      <div
        className={clsx(classes.permissionRow, isConflict && classes.permissionRowConflict)}
        key={permission.system_name}>
        <FormControlLabel
          control={
            <AppCheckbox
              variant="rectangle"
              color="primary"
              checked={checked}
              disabled={disabled || locked}
              onChange={e => onToggle(permission.system_name, e.target.checked)}
            />
          }
          label={
            <span className={clsx(classes.permissionLabel, locked && classes.permissionLabelLocked)}>
              {permission.display_name}
            </span>
          }
        />
        {!!permission.description && <DetailsIcon size="14px" content={<>{permission.description}</>} />}
        {locked && <span className={clsx(classes.badge, classes.badgeLocked)}>Locked</span>}
        {!locked && addedSet.has(permission.system_name) && (
          <span className={clsx(classes.badge, classes.badgeAdded)}>Added</span>
        )}
        {!locked && removedSet.has(permission.system_name) && (
          <span className={clsx(classes.badge, classes.badgeRemoved)}>Removed</span>
        )}
        {!locked &&
          changedFromDefault &&
          !addedSet.has(permission.system_name) &&
          !removedSet.has(permission.system_name) && (
            <span className={clsx(classes.badge, classes.badgeAdded)}>Changed</span>
          )}
      </div>
    );

    if (!reasons.length) return row;
    return (
      <Tooltip key={permission.system_name} title={`Required by ${reasons.join(', ')}`}>
        <span>{row}</span>
      </Tooltip>
    );
  };

  return (
    <Box>
      {groups.map(group => (
        <div className={classes.permissionGroup} key={group.group}>
          {!!group.group && <div className={classes.permissionGroupTitle}>{group.group}</div>}
          {(group.permissions || []).map(renderRow)}
        </div>
      ))}
    </Box>
  );
};
